import React from 'react'
import moment from 'moment/moment'
import { DATE_DISPLAY_FORMAT } from '../constants/dateFormats'
import { VEHICLE_TYPE } from '../constants/global'
import appUserService from '../services/appUserService'
import { useAppUserContext } from './AppUserContext'

const TrafficFineVehicleContext = React.createContext(null)

const VEHICLE_TYPE_PRIORITY = {
  [VEHICLE_TYPE.CAR]: 0,
  [VEHICLE_TYPE.MOTOBIKE]: 1,
  [VEHICLE_TYPE.ELECTRIC_MOTORBIKE]: 2
}

const DEFAULT_VEHICLE_STATE = {
  userKey: '',
  listVehicleRegisteredPackage: [],
  listVehicleNotRegistered: [],
  isLoadingVehicleList: false,
  isVehicleListNetworkError: false
}

function createVehicleListResult({ success = false, registeredVehicles = [], notRegisteredVehicles = [], isNetworkError = false } = {}) {
  return {
    success,
    registeredVehicles,
    notRegisteredVehicles,
    isNetworkError
  }
}

function getVehicleTypePriority(vehicleType) {
  return VEHICLE_TYPE_PRIORITY[Number(vehicleType)] ?? 999
}

function getSortTimeValue(timeValue, fallbackValue = 0) {
  if (timeValue === null || timeValue === undefined || timeValue === '') {
    return Number(fallbackValue) || 0
  }

  const parsedTime = Date.parse(timeValue)
  if (!Number.isNaN(parsedTime)) {
    return parsedTime
  }

  const numericTime = Number(timeValue)
  return Number.isNaN(numericTime) ? Number(fallbackValue) || 0 : numericTime
}

function sortRegisteredVehicles(vehicles = []) {
  return [...vehicles].sort((vehicleA, vehicleB) => {
    const priorityDiff = getVehicleTypePriority(vehicleA?.vehicleType) - getVehicleTypePriority(vehicleB?.vehicleType)
    if (priorityDiff !== 0) {
      return priorityDiff
    }

    const createdAtDiff =
      getSortTimeValue(vehicleB?.createdAt, vehicleB?.appUserVehicleNotifyInfoId) -
      getSortTimeValue(vehicleA?.createdAt, vehicleA?.appUserVehicleNotifyInfoId)

    if (createdAtDiff !== 0) {
      return createdAtDiff
    }

    return Number(vehicleB?.appUserVehicleNotifyInfoId || 0) - Number(vehicleA?.appUserVehicleNotifyInfoId || 0)
  })
}

async function requestVehicleListResult(phoneNumber, hasSdkToken) {
  const [registeredResponse, notRegisteredResponse] = await Promise.all([
    appUserService.AppUserVehicleNotifyInfoPartnerGetList({
      filter: {
        ...(hasSdkToken ? {} : { phoneNumber })
      },
      skip: 0,
      limit: 100,
      startDate: moment().format(DATE_DISPLAY_FORMAT)
    }),
    appUserService.CustomerCriminalLookupHistoryPartnerGetList({
      filter: {
        ...(hasSdkToken ? {} : { phoneNumber }),
        onlyNoPackage: 1
      },
      skip: 0,
      limit: 5,
      order: {
        key: 'updatedAt',
        value: 'desc'
      }
    })
  ])

  const registeredVehicles = registeredResponse?.statusCode === 200 ? sortRegisteredVehicles(registeredResponse?.data?.data || []) : []
  const notRegisteredVehicles = notRegisteredResponse?.statusCode === 200 ? notRegisteredResponse?.data?.data || [] : []
  const isNetworkError = Boolean(registeredResponse?.isNetworkError || notRegisteredResponse?.isNetworkError)

  return createVehicleListResult({
    success: registeredResponse?.statusCode === 200 && notRegisteredResponse?.statusCode === 200,
    registeredVehicles,
    notRegisteredVehicles,
    isNetworkError
  })
}

export const TrafficFineVehicleContextProvider = ({ children }) => {
  const { userData } = useAppUserContext()
  const [vehicleState, setVehicleState] = React.useState(DEFAULT_VEHICLE_STATE)
  const vehicleListRequestRef = React.useRef(null)

  const userPhoneNumber = React.useMemo(() => {
    return userData?.phoneNumberWithUsernamePrefix || ''
  }, [userData?.phoneNumberWithUsernamePrefix])

  const userKey = React.useMemo(() => {
    return userData?.sdkToken || userData?.phoneNumberWithUsernamePrefix || ''
  }, [userData?.sdkToken, userData?.phoneNumberWithUsernamePrefix])

  const updateVehicleStateByUserKey = React.useCallback((requestUserKey, updater) => {
    setVehicleState((prevState) => {
      if (prevState.userKey !== requestUserKey) {
        return prevState
      }

      return updater(prevState)
    })
  }, [])

  const mergeVehicleStateByUserKey = React.useCallback(
    (requestUserKey, nextState) => {
      updateVehicleStateByUserKey(requestUserKey, (prevState) => ({
        ...prevState,
        ...nextState
      }))
    },
    [updateVehicleStateByUserKey]
  )

  React.useEffect(() => {
    vehicleListRequestRef.current = null

    setVehicleState((prevState) => {
      if (prevState.userKey === userKey) {
        return prevState
      }

      return {
        ...DEFAULT_VEHICLE_STATE,
        userKey
      }
    })
  }, [userKey])

  const getVehicleLists = React.useCallback(async () => {
    if (!userKey) {
      vehicleListRequestRef.current = null
      setVehicleState(DEFAULT_VEHICLE_STATE)

      return createVehicleListResult()
    }

    const requestUserKey = userKey
    const pendingRequest = vehicleListRequestRef.current

    if (pendingRequest?.userKey === requestUserKey) {
      return pendingRequest.promise
    }

    const requestPromise = (async () => {
      mergeVehicleStateByUserKey(requestUserKey, {
        isLoadingVehicleList: true,
        isVehicleListNetworkError: false
      })

      try {
        const hasSdkToken = !!userData?.sdkToken
        const vehicleListResult = await requestVehicleListResult(userPhoneNumber, hasSdkToken)

        mergeVehicleStateByUserKey(requestUserKey, {
          listVehicleRegisteredPackage: vehicleListResult.registeredVehicles,
          listVehicleNotRegistered: vehicleListResult.notRegisteredVehicles,
          isLoadingVehicleList: false,
          isVehicleListNetworkError: vehicleListResult.isNetworkError
        })

        return vehicleListResult
      } catch (error) {
        console.error(error)

        mergeVehicleStateByUserKey(requestUserKey, {
          listVehicleRegisteredPackage: [],
          listVehicleNotRegistered: [],
          isLoadingVehicleList: false,
          isVehicleListNetworkError: true
        })

        return createVehicleListResult({ isNetworkError: true })
      } finally {
        if (vehicleListRequestRef.current?.promise === requestPromise) {
          vehicleListRequestRef.current = null
        }
      }
    })()

    vehicleListRequestRef.current = {
      userKey: requestUserKey,
      promise: requestPromise
    }

    return requestPromise
  }, [mergeVehicleStateByUserKey, userKey, userPhoneNumber])

  React.useEffect(() => {
    if (!userKey) {
      return
    }

    void getVehicleLists()
  }, [getVehicleLists, userKey])

  const vehicleList = React.useMemo(
    () => [...vehicleState.listVehicleRegisteredPackage, ...vehicleState.listVehicleNotRegistered],
    [vehicleState.listVehicleNotRegistered, vehicleState.listVehicleRegisteredPackage]
  )

  const value = React.useMemo(
    () => ({
      listVehicleRegisteredPackage: vehicleState.listVehicleRegisteredPackage,
      listVehicleNotRegistered: vehicleState.listVehicleNotRegistered,
      vehicleList,
      isLoadingVehicleList: vehicleState.isLoadingVehicleList,
      isVehicleListNetworkError: vehicleState.isVehicleListNetworkError,
      getVehicleLists
    }),
    [
      getVehicleLists,
      vehicleList,
      vehicleState.isLoadingVehicleList,
      vehicleState.isVehicleListNetworkError,
      vehicleState.listVehicleNotRegistered,
      vehicleState.listVehicleRegisteredPackage
    ]
  )

  return <TrafficFineVehicleContext.Provider value={value}>{children}</TrafficFineVehicleContext.Provider>
}

export const useTrafficFineVehicleContext = () => {
  const context = React.useContext(TrafficFineVehicleContext)

  if (!context) {
    throw new Error('useTrafficFineVehicleContext must be used within TrafficFineVehicleContextProvider')
  }

  return context
}
